import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, X, Info, Gift } from 'lucide-react';
import Swal from 'sweetalert2'; // <-- Import SweetAlert2
import 'sweetalert2/dist/sweetalert2.min.css'; // <-- Import the styles
import './Coupon.css';

const COUPON_API_URL = process.env.REACT_APP_API_URL;

// Reusable authenticated fetch utility (no changes here)
const fetchWithAuth = async (url, options = {}) => {
  const token = localStorage.getItem('adminToken');
  const headers = {
    'Content-Type': 'application/json',
    ...(options.headers || {}),
  };
  
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const fetchOptions = {
    ...options,
    headers: headers,
  };

  try {
    const response = await fetch(url, fetchOptions);

    if (response.ok) {
      const text = await response.text();
      return text ? JSON.parse(text) : {};
    }

    let errorMessage;
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || 'An API error occurred.';
    } catch (e) {
      const textError = await response.text();
      errorMessage = textError || `API Error: ${response.status} ${response.statusText}`;
    }
    
    throw new Error(errorMessage);

  } catch (error) {
    throw error;
  }
};

// Reusable currency formatting function (no changes here)
const formatCurrency = (amount) => {
    if (typeof amount !== 'number') {
        return '₹0.00';
    }
    return `₹${amount.toFixed(2)}`;
};

// --- Modal for Creating/Editing Coupons ---
const CouponModal = ({ isOpen, onClose, onSave, editingCoupon }) => {
  const [couponData, setCouponData] = useState({});
  const [services, setServices] = useState([]);

  const fetchServices = useCallback(async () => {
    try {
      const data = await fetchWithAuth(`${COUPON_API_URL}/api/payment-packages`);
      setServices(data.packages || data || []);
    } catch (err) {
      console.error("Failed to fetch services:", err);
      // MODIFIED: Replaced alert with Swal
      Swal.fire({
        icon: 'error',
        title: 'Failed to Load Services',
        text: 'Could not load the list of report services. Please check the connection and try again.',
      });
    }
  }, []);

  // ... The rest of the CouponModal component remains the same ...
  useEffect(() => {
    if (isOpen) {
      fetchServices();
      const initialData = editingCoupon
        ? { ...editingCoupon, applicableServices: editingCoupon.applicableServices?.map(s => s._id) || [] }
        : {
            code: '', description: '', discountType: 'percentage', discountValue: 10, isActive: true,
            serviceType: 'report', activationDate: new Date().toISOString().split('T')[0],
            validUntil: new Date(new Date().setDate(new Date().getDate() + 30)).toISOString().split('T')[0],
            usageLimit: 100, userUsageLimit: 1, applicableServices: [],
          };
      if (initialData.activationDate) initialData.activationDate = initialData.activationDate.split('T')[0];
      if (initialData.validUntil) initialData.validUntil = initialData.validUntil.split('T')[0];
      setCouponData(initialData);
    }
  }, [isOpen, editingCoupon, fetchServices]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValues = { [name]: type === 'checkbox' ? checked : value };
    if (name === 'serviceType' && value === 'counselling') newValues.applicableServices = [];
    setCouponData(prev => ({ ...prev, ...newValues }));
  };

  const handleServiceToggle = (serviceId) => {
    setCouponData(prev => {
      const currentServices = prev.applicableServices || [];
      const newServices = currentServices.includes(serviceId) ? currentServices.filter(id => id !== serviceId) : [...currentServices, serviceId];
      return { ...prev, applicableServices: newServices };
    });
  };
  const handleAllServicesToggle = () => {
      setCouponData(prev => ({ ...prev, applicableServices: (prev.applicableServices.length === 0 && services.length > 0) ? services.map(s => s._id) : [] }));
  }
  const handleSave = (e) => { e.preventDefault(); onSave(couponData); };
  if (!isOpen) return null;

  return (
    <div className="c-coupon-modal-overlay">
      <div className="c-coupon-modal-content" style={{maxWidth: '800px'}}>
        <form onSubmit={handleSave}>
          <div className="c-coupon-modal-header"><h3>{editingCoupon ? 'Edit Coupon' : 'Create New Coupon'}</h3></div>
          <div className="c-coupon-form">
            <div className="c-coupon-form-group full-width"><label htmlFor="description">Description</label><input type="text" id="description" name="description" value={couponData.description || ''} onChange={handleChange} required /></div>
            <div className="c-coupon-form-grid">
              <div className="c-coupon-form-group"><label htmlFor="code">Coupon Code</label><input type="text" id="code" name="code" value={couponData.code || ''} onChange={handleChange} required style={{ textTransform: 'uppercase' }} /></div>
              <div className="c-coupon-form-group"><label htmlFor="discountType">Discount Type</label><select id="discountType" name="discountType" value={couponData.discountType || 'percentage'} onChange={handleChange}><option value="percentage">Percentage</option><option value="fixed">Fixed Amount</option></select></div>
              <div className="c-coupon-form-group"><label htmlFor="discountValue">Value ({couponData.discountType === 'percentage' ? '%' : '₹'})</label><input type="number" id="discountValue" name="discountValue" value={couponData.discountValue || ''} onChange={handleChange} required min="0" /></div>
              <div className="c-coupon-form-group"><label htmlFor="usageLimit">Global Usage Limit</label><input type="number" id="usageLimit" name="usageLimit" value={couponData.usageLimit || ''} onChange={handleChange} required min="1" /></div>
              <div className="c-coupon-form-group"><label htmlFor="userUsageLimit">Limit Per User</label><input type="number" id="userUsageLimit" name="userUsageLimit" value={couponData.userUsageLimit || ''} onChange={handleChange} required min="1" /></div>
              <div className="c-coupon-form-group"><label htmlFor="activationDate">Activation Date</label><input type="date" id="activationDate" name="activationDate" value={couponData.activationDate || ''} onChange={handleChange} required /></div>
              <div className="c-coupon-form-group"><label htmlFor="validUntil">Expiry Date</label><input type="date" id="validUntil" name="validUntil" value={couponData.validUntil || ''} onChange={handleChange} required /></div>
            </div>
            <div className="c-coupon-form-group full-width"><label>Service Type</label><div style={{ display: 'flex', gap: '1rem' }}><label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}><input type="radio" name="serviceType" value="report" checked={couponData.serviceType === 'report'} onChange={handleChange} /> Report Payment</label><label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}><input type="radio" name="serviceType" value="counselling" checked={couponData.serviceType === 'counselling'} onChange={handleChange} /> Counselling Payment</label></div></div>
            {couponData.serviceType === 'report' && (<div className="c-coupon-form-group full-width"><label>Applicable Report Services</label><div className="c-coupon-checkbox-group"><div className="c-coupon-checkbox-item"><input type="checkbox" id="all-services" checked={(couponData.applicableServices || []).length === 0} onChange={handleAllServicesToggle} /><label htmlFor="all-services"><strong>All Services</strong></label></div>{services.map(service => (<div key={service._id} className="c-coupon-checkbox-item"><input type="checkbox" id={service._id} checked={(couponData.applicableServices || []).includes(service._id)} onChange={() => handleServiceToggle(service._id)} /><label htmlFor={service._id}>{service.name}</label></div>))}</div></div>)}
            <div className="c-coupon-form-group"><label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}><input type="checkbox" name="isActive" checked={couponData.isActive} onChange={handleChange} style={{width: 'auto'}} />Activate Coupon</label></div>
          </div>
          <div className="c-coupon-modal-footer"><button type="button" className="c-coupon-btn c-coupon-btn-secondary" onClick={onClose}>Cancel</button><button type="submit" className="c-coupon-btn c-coupon-btn-primary">Save Coupon</button></div>
        </form>
      </div>
    </div>
  );
};

// --- Modal for Viewing Coupon Usage Details (Unchanged) ---
const UsageDetailsModal = ({ isOpen, onClose, coupon }) => {
    // ... no changes needed in this component
    const [details, setDetails] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (isOpen && coupon) {
            const fetchUsageDetails = async () => {
                setLoading(true);
                setError(null);
                try {
                    const data = await fetchWithAuth(`${COUPON_API_URL}/api/coupons/${coupon._id}/usage`);
                    setDetails(data.usageDetails || []);
                } catch (err) {
                    setError(err.message);
                } finally {
                    setLoading(false);
                }
            };
            fetchUsageDetails();
        }
    }, [isOpen, coupon]);

    if (!isOpen) return null;

    return (
        <div className="c-coupon-modal-overlay">
            <div className="c-coupon-modal-content" style={{ maxWidth: '1000px' }}>
                <div className="c-coupon-modal-header"><h3>Usage Details for "{coupon.code}"</h3><button type="button" className="c-coupon-modal-close-btn" onClick={onClose}><X size={20} /></button></div>
                <div className="c-coupon-modal-body">
                    {loading && <p>Loading usage details...</p>}
                    {error && <p style={{ color: 'red' }}>Error: {error}</p>}
                    {!loading && !error && (
                        details.length > 0 ? (
                            <table className="c-coupon-table c-coupon-usage-details-table">
                                <thead><tr><th>S.No.</th><th>User Name</th><th>User Email</th><th>Service Used</th><th>Original Amount</th><th>Discount</th><th>Date Used</th></tr></thead>
                                <tbody>{details.map((usage, index) => (<tr key={usage._id || index}><td>{index + 1}</td><td>{usage.user?.name || 'N/A'}</td><td>{usage.user?.email || 'N/A'}</td><td>{usage.service?.name || 'N/A'}</td><td>{formatCurrency(usage.originalAmount)}</td><td>{formatCurrency(usage.discountAmount)}</td><td>{new Date(usage.usedAt).toLocaleString()}</td></tr>))}</tbody>
                            </table>
                        ) : (<p style={{ textAlign: 'center', padding: '2rem' }}>No usage records found for this coupon.</p>)
                    )}
                </div>
                <div className="c-coupon-modal-footer"><button type="button" className="c-coupon-btn c-coupon-btn-secondary" onClick={onClose}>Close</button></div>
            </div>
        </div>
    );
};


// --- MAIN COUPON PAGE COMPONENT ---
const Coupon = () => {
  const [coupons, setCoupons] = useState([]);
  const [usageCounts, setUsageCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [isUsageModalOpen, setIsUsageModalOpen] = useState(false);
  const [selectedCouponForUsage, setSelectedCouponForUsage] = useState(null);
  const navigate = useNavigate();

  const fetchCouponData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [couponsResponse, countsResponse] = await Promise.all([
        fetchWithAuth(`${COUPON_API_URL}/api/coupons`),
        fetchWithAuth(`${COUPON_API_URL}/api/coupons/usage-counts`)
      ]);
      setCoupons(couponsResponse);
      setUsageCounts(countsResponse);
    } catch (err) { setError(err.message); } 
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchCouponData(); }, [fetchCouponData]);

  const handleCreateNew = () => { setEditingCoupon(null); setIsModalOpen(true); };
  const handleEdit = (coupon) => { setEditingCoupon(coupon); setIsModalOpen(true); };
  const handleUsageClick = (coupon) => { setSelectedCouponForUsage(coupon); setIsUsageModalOpen(true); };

  const handleSave = async (couponData) => {
    const url = couponData._id ? `${COUPON_API_URL}/api/coupons/${couponData._id}` : `${COUPON_API_URL}/api/coupons`;
    const method = couponData._id ? 'PUT' : 'POST';
    try {
      const result = await fetchWithAuth(url, { method, body: JSON.stringify(couponData) });
      // MODIFIED: Replaced alert with Swal
      Swal.fire({
        icon: 'success',
        title: 'Saved!',
        text: result.message || 'Coupon has been saved successfully.',
        timer: 2000,
        showConfirmButton: false,
      });
      setIsModalOpen(false);
      fetchCouponData();
    } catch (err) {
      // MODIFIED: Replaced alert with Swal
      Swal.fire({
        icon: 'error',
        title: 'Save Failed',
        text: err.message || 'An unknown error occurred while saving.',
      });
    }
  };

  const handleDelete = (couponId) => {
    // MODIFIED: Replaced window.confirm with Swal
    Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const deleteResult = await fetchWithAuth(`${COUPON_API_URL}/api/coupons/${couponId}`, { method: 'DELETE' });
          Swal.fire(
            'Deleted!',
            deleteResult.message || 'The coupon has been deleted.',
            'success'
          );
          fetchCouponData();
        } catch (err) {
          Swal.fire(
            'Error!',
            err.message || 'Could not delete the coupon.',
            'error'
          );
        }
      }
    });
  };
  
  const handleToggleActive = async (couponId, currentStatus) => {
    try {
      await fetchWithAuth(`${COUPON_API_URL}/api/coupons/${couponId}`, {
        method: 'PUT',
        body: JSON.stringify({ isActive: !currentStatus }),
      });
      // MODIFIED: Use a small toast for non-critical success messages
      const Toast = Swal.mixin({
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });

      Toast.fire({
        icon: 'success',
        title: `Coupon ${!currentStatus ? 'activated' : 'deactivated'}`,
      });
      fetchCouponData();
    } catch (err) {
      // MODIFIED: Replaced alert with Swal
      Swal.fire({
        icon: 'error',
        title: 'Update Failed',
        text: `Error updating coupon status: ${err.message}`,
      });
    }
  };
  // ... The rest of the Coupon component's functions and JSX are unchanged ...
  const formatDate = (dateString) => new Date(dateString).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
  const getPageTitle = () => {
    if (isModalOpen) return editingCoupon ? `Edit: "${editingCoupon.code}"` : 'Create New Coupon';
    if (isUsageModalOpen && selectedCouponForUsage) return `Usage Details: "${selectedCouponForUsage.code}"`;
    return 'Coupon Management';
  };

  return (
    <div className="c-coupon-page">
      <Helmet><title>{`${getPageTitle()} | GN VISA Admin`}</title><meta name="description" content="Create, view, and manage all payment and refund coupons and track their usage." /></Helmet>
      <div className="c-coupon-header"><button className="c-coupon-btn c-coupon-btn-primary" onClick={handleCreateNew}><Plus size={18} /> Create Payment Coupon</button><button className="c-coupon-btn c-coupon-btn-secondary" onClick={() => navigate('/admin/refund-coupons')}><Gift size={18} /> Manage Refund Coupons</button></div>
      {loading && <p>Loading...</p>}
      {error && <p style={{ color: 'red' }}>Error: {error}</p>}
      {!loading && !error && (
        <div className="c-coupon-table-wrapper">
          <table className="c-coupon-table">
            <thead><tr><th>Code</th><th>Applies To</th><th>Discount</th><th>Status</th><th>Schedule</th><th>Usage</th><th>Active</th><th>Actions</th></tr></thead>
            <tbody>
              {coupons.length > 0 ? (coupons.map(coupon => {
                    const realTimeUsage = usageCounts[coupon.code] || 0;
                    const now = new Date();
                    const activationDate = new Date(coupon.activationDate);
                    const expiryDate = new Date(coupon.validUntil);
                    const isUpcoming = activationDate > now;
                    const isExpired = expiryDate < now;
                    const statusText = !coupon.isActive ? "Inactive" : isExpired ? "Expired" : isUpcoming ? "Scheduled" : "Active";
                    const statusClass = !coupon.isActive || isExpired ? "c-coupon-status-inactive" : isUpcoming ? "c-coupon-status-scheduled" : "c-coupon-status-active";
                    return (
                      <tr key={coupon._id}>
                        <td><span className="c-coupon-code">{coupon.code}</span></td>
                        <td>{coupon.serviceType === 'counselling' ? 'Counselling Service' : (coupon.applicableServices && coupon.applicableServices.length > 0 ? coupon.applicableServices.map(s => s.name).join(', ') : 'All Report Services')}</td>
                        <td>{coupon.discountType === 'percentage' ? `${coupon.discountValue}%` : formatCurrency(coupon.discountValue)}</td>
                        <td><span className={`c-coupon-status-badge ${statusClass}`}>{statusText}</span></td>
                        <td>{formatDate(coupon.activationDate)} - {formatDate(coupon.validUntil)}</td>
                        <td><button className="c-coupon-usage-link" onClick={() => handleUsageClick(coupon)} disabled={realTimeUsage === 0}>{realTimeUsage} / {coupon.usageLimit}{realTimeUsage > 0 && <Info size={14} />}</button><div style={{ fontSize: '0.75rem', color: '#555', marginTop: '4px' }}>(Per User: {coupon.userUsageLimit || 1})</div></td>
                        <td><label className="c-coupon-toggle-switch"><input type="checkbox" checked={coupon.isActive} onChange={() => handleToggleActive(coupon._id, coupon.isActive)} /><span className="c-coupon-slider"></span></label></td>
                        <td className="c-coupon-actions"><button className="c-coupon-btn c-coupon-btn-secondary" onClick={() => handleEdit(coupon)}><Edit size={16} /></button><button className="c-coupon-btn c-coupon-btn-danger" onClick={() => handleDelete(coupon._id)}><Trash2 size={16} /></button></td>
                      </tr>
                    )
                })) : (<tr><td colSpan="8" style={{ textAlign: 'center', padding: '2rem' }}>No payment coupons found. Create one!</td></tr>)}
            </tbody>
          </table>
        </div>
      )}
      <CouponModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onSave={handleSave} editingCoupon={editingCoupon}/>
      <UsageDetailsModal isOpen={isUsageModalOpen} onClose={() => setIsUsageModalOpen(false)} coupon={selectedCouponForUsage} />
    </div>
  );
};

export default Coupon;